import React, { useEffect, useRef, useState } from "react";
import { Plus, Play, Clock } from "lucide-react";
import { SmartImage, ViewportVideo } from "./media";
import { brl, discountPct } from "../../lib/format";

function SectionHead({ eyebrow, title }) {
  return (
    <div className="px-5 mb-3">
      <p className="text-[11px] font-bold tracking-[0.2em] hmmm-text">{eyebrow}</p>
      <h2 className="font-head text-2xl font-extrabold tracking-tight -mt-0.5 menu-text">{title}</h2>
    </div>
  );
}

// Auto-scrolling seamless marquee; pauses on interaction & out of viewport; respects reduced motion.
export function BestSellers({ items, onOpen, onAdd }) {
  const trackRef = useRef(null);
  const rafRef = useRef(null);
  const pausedUntil = useRef(0);
  const inView = useRef(true);

  useEffect(() => {
    if (!items?.length) return;
    const el = trackRef.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;

    const io = new IntersectionObserver(([e]) => { inView.current = e.isIntersecting; }, { threshold: 0.1 });
    io.observe(el);

    const step = () => {
      if (inView.current && Date.now() > pausedUntil.current) {
        el.scrollLeft += 0.5;
        const half = el.scrollWidth / 2;
        if (el.scrollLeft >= half) el.scrollLeft -= half;
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    const pause = () => { pausedUntil.current = Date.now() + 2500; };
    el.addEventListener("pointerdown", pause);
    el.addEventListener("touchstart", pause, { passive: true });
    el.addEventListener("wheel", pause, { passive: true });
    return () => { cancelAnimationFrame(rafRef.current); io.disconnect(); el.removeEventListener("pointerdown", pause); el.removeEventListener("touchstart", pause); el.removeEventListener("wheel", pause); };
  }, [items]);

  if (!items?.length) return null;
  const loop = items.length > 1 ? [...items, ...items] : items;
  return (
    <section className="pt-6" data-testid="section-best-sellers">
      <SectionHead eyebrow="OS QUERIDINHOS" title="Mais Vendidos" />
      <div ref={trackRef} className="flex gap-3 overflow-x-auto no-scrollbar px-5 pb-2">
        {loop.map((p, i) => {
          const promo = p.promo_price && p.promo_price < p.price;
          const price = promo ? p.promo_price : p.price;
          const pct = discountPct(p.price, p.promo_price);
          const rank = (i % items.length) + 1;
          return (
            <div key={`${p.id}-${i}`} className="shrink-0 w-[74%] max-w-[280px] rounded-2xl menu-elevated border menu-border overflow-hidden" data-testid={`best-card-${p.id}`}>
              <button onClick={() => onOpen(p)} className="block w-full text-left">
                <div className="relative h-40">
                  <SmartImage src={p.image} className="h-full w-full" />
                  <span className="absolute top-2 left-2 text-[10px] font-bold hmmm-gradient text-white px-2 py-0.5 rounded-full">Top {rank}</span>
                  {pct > 0 && <span className="absolute top-2 right-2 text-[10px] font-bold bg-rose-600 text-white px-2 py-0.5 rounded-full">-{pct}%</span>}
                </div>
              </button>
              <div className="p-3">
                <div className="flex items-center gap-1 text-[11px] menu-muted mb-1"><Clock size={12} /> {p.prep_time} min</div>
                <button onClick={() => onOpen(p)} className="text-left w-full">
                  <h3 className="font-head font-bold menu-text leading-snug line-clamp-1">{p.name}</h3>
                  <p className="text-xs menu-muted line-clamp-2 mt-0.5 min-h-[32px]">{p.description}</p>
                </button>
                <div className="flex items-end justify-between mt-2">
                  <div>
                    {promo && <span className="text-xs menu-faint line-through mr-1">{brl(p.price)}</span>}
                    <span className="font-head text-lg font-extrabold price-final">{brl(price)}</span>
                  </div>
                  <AddBtn disabled={p.sold_out} onClick={() => onAdd(p)} tid={`best-add-${p.id}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function VideoMenu({ items, products, onOpen }) {
  if (!items?.length) return null;
  const byId = Object.fromEntries((products || []).map((p) => [p.id, p]));
  return (
    <section className="pt-6" data-testid="section-video-menu">
      <SectionHead eyebrow="NA CHAPA & BASTIDORES" title="Menu em Vídeo" />
      <div className="flex gap-3 overflow-x-auto no-scrollbar px-5 pb-2 snap-x snap-mandatory">
        {items.map((v) => <VideoCard key={v.id} v={v} prod={byId[v.product_id]} onOpen={onOpen} />)}
      </div>
    </section>
  );
}

function VideoCard({ v, prod, onOpen }) {
  const [playing, setPlaying] = useState(false);
  return (
    <div className="snap-start shrink-0 w-[58%] max-w-[220px] rounded-2xl overflow-hidden menu-elevated border menu-border" data-testid={`video-card-${v.id}`}>
      <div className="relative h-[260px]">
        {playing ? (
          <ViewportVideo src={v.video} poster={v.thumbnail} className="h-full w-full object-cover" />
        ) : (
          <>
            <SmartImage src={v.thumbnail} className="h-full w-full" />
            <button onClick={() => setPlaying(true)} data-testid={`video-play-${v.id}`} className="absolute inset-0 flex items-center justify-center">
              <span className="h-14 w-14 rounded-full bg-black/50 backdrop-blur flex items-center justify-center border border-white/30">
                <Play className="text-white ml-0.5" fill="white" size={22} />
              </span>
            </button>
          </>
        )}
        {v.duration && <span className="absolute bottom-2 right-2 text-[10px] bg-black/70 text-white px-1.5 py-0.5 rounded">{v.duration}</span>}
      </div>
      <div className="p-3">
        <h3 className="font-head font-bold text-sm leading-snug line-clamp-1 menu-text">{v.title}</h3>
        {prod && (
          <div className="flex items-center justify-between mt-2">
            <span className="price-final font-head font-bold text-sm">{brl(prod.promo_price || prod.price)}</span>
            <button onClick={() => onOpen(prod)} data-testid={`video-ver-${v.id}`} className="text-xs font-bold menu-chip menu-text px-3 py-1.5 rounded-full">VER</button>
          </div>
        )}
      </div>
    </div>
  );
}

export function Offers({ items, onOpen, onAdd }) {
  if (!items?.length) return null;
  return (
    <section className="pt-6" data-testid="section-offers">
      <SectionHead eyebrow="TEMPO LIMITADO" title="Ofertas Especiais" />
      <div className="flex gap-3 overflow-x-auto no-scrollbar px-5 pb-2 snap-x snap-mandatory">
        {items.map((p) => {
          const pct = discountPct(p.price, p.promo_price);
          const save = (p.price - p.promo_price) || 0;
          return (
            <div key={p.id} className="snap-start shrink-0 w-[62%] max-w-[240px] rounded-2xl menu-elevated border border-rose-600/25 overflow-hidden" data-testid={`offer-card-${p.id}`}>
              <button onClick={() => onOpen(p)} className="block w-full text-left relative">
                <div className="h-32"><SmartImage src={p.image} className="h-full w-full" /></div>
                <span className="absolute top-2 left-2 text-[11px] font-extrabold bg-rose-600 text-white px-2 py-0.5 rounded-full">-{pct}%</span>
              </button>
              <div className="p-3">
                <h3 className="font-head font-bold text-sm line-clamp-1 menu-text">{p.name}</h3>
                <p className="text-xs menu-muted line-clamp-1 mt-0.5">{p.description}</p>
                <div className="flex items-end justify-between mt-2">
                  <div>
                    <span className="text-xs menu-faint line-through mr-1">{brl(p.price)}</span>
                    <span className="font-head text-base font-extrabold price-final">{brl(p.promo_price)}</span>
                    <p className="text-[10px] text-emerald-500 font-semibold">Economize {brl(save)}</p>
                  </div>
                  <AddBtn disabled={p.sold_out} onClick={() => onAdd(p)} tid={`offer-add-${p.id}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function AddBtn({ onClick, disabled, tid }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={tid}
      className={`h-9 w-9 shrink-0 rounded-xl flex items-center justify-center transition-transform active:scale-90 ${disabled ? "bg-zinc-800 text-zinc-600" : "hmmm-gradient text-white hmmm-glow"}`}>
      <Plus size={20} strokeWidth={2.6} />
    </button>
  );
}
